import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

const ProjectHubContext = createContext(null);
const STORAGE_KEY = 'ihg_projects';

function loadProjects() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch { return []; }
}

function saveProjects(projects) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
}

export function ProjectHubProvider({ children }) {
  const [projects, setProjects] = useState(loadProjects);

  useEffect(() => { saveProjects(projects); }, [projects]);

  const createProject = useCallback((name, description = '') => {
    const id = `proj_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
    const project = { id, name, description, finishes: [], created_at: new Date().toISOString(), updated_at: new Date().toISOString() };
    setProjects(prev => [project, ...prev]);
    return id;
  }, []);

  const deleteProject = useCallback((id) => {
    setProjects(prev => prev.filter(p => p.id !== id));
  }, []);

  const updateProject = useCallback((id, updates) => {
    setProjects(prev => prev.map(p => p.id === id ? { ...p, ...updates, updated_at: new Date().toISOString() } : p));
  }, []);

  const addFinishToProject = useCallback((projectId, finish) => {
    setProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;
      if (p.finishes.find(f => f.id === finish.id)) return p;
      return { ...p, finishes: [...p.finishes, { ...finish, notes: '', added_at: new Date().toISOString() }], updated_at: new Date().toISOString() };
    }));
  }, []);

  const removeFinishFromProject = useCallback((projectId, finishId) => {
    setProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;
      return { ...p, finishes: p.finishes.filter(f => f.id !== finishId), updated_at: new Date().toISOString() };
    }));
  }, []);

  const updateFinishNotes = useCallback((projectId, finishId, notes) => {
    setProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;
      return { ...p, finishes: p.finishes.map(f => f.id === finishId ? { ...f, notes } : f), updated_at: new Date().toISOString() };
    }));
  }, []);

  const reorderFinishes = useCallback((projectId, fromIndex, toIndex) => {
    setProjects(prev => prev.map(p => {
      if (p.id !== projectId) return p;
      const arr = [...p.finishes];
      const [item] = arr.splice(fromIndex, 1);
      arr.splice(toIndex, 0, item);
      return { ...p, finishes: arr, updated_at: new Date().toISOString() };
    }));
  }, []);

  const getProject = useCallback((id) => projects.find(p => p.id === id), [projects]);

  const isFinishInProject = useCallback((projectId, finishId) => {
    const p = projects.find(pr => pr.id === projectId);
    return p ? p.finishes.some(f => f.id === finishId) : false;
  }, [projects]);

  const totalFinishes = projects.reduce((sum, p) => sum + p.finishes.length, 0);

  return (
    <ProjectHubContext.Provider value={{
      projects, createProject, deleteProject, updateProject,
      addFinishToProject, removeFinishFromProject, updateFinishNotes,
      reorderFinishes, getProject, isFinishInProject, totalFinishes
    }}>
      {children}
    </ProjectHubContext.Provider>
  );
}

export function useProjectHub() {
  const ctx = useContext(ProjectHubContext);
  if (!ctx) throw new Error('useProjectHub must be used within ProjectHubProvider');
  return ctx;
}
